// GlanceTab - "At a Glance", the first tab a coordination project opens
// on (docs/project-at-a-glance.md). One page answering in under a minute:
// what is this building, where are the systems, how does it run through a
// day, how did we get here, and what else matters.
//
// Everything is drawn from ONE manifest (GET /projects/:id/glance): the
// stored version the owner published (geometry, systems, roof,
// operations, notes) merged with a live layer computed from the
// project's own schedule, sheets and issues. Sections with no data say
// what would fill them instead of guessing.
import { roomSearchUrl } from "./searchLink";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Button, Dropdown, Skeleton, Tooltip, message } from "antd";
import { DownOutlined, FileExcelOutlined, FileTextOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { url, tokenRef } from "api";
import FactStrip from "./FactStrip";
import ExplodedStack from "./ExplodedStack";
import { DayControls, DayProfile, statusSentence } from "./DayScrubber";
import RoofTable from "./RoofTable";
import GlanceThemeToggle from "./GlanceThemeToggle";
import TimelineLanes from "./TimelineLanes";
import PlanView from "./PlanView";
import SourceNotes from "./SourceNotes";
import TrackedCategories from "./views/TrackedCategories";
import ModelLayer from "./ModelLayer";
import UploadDocumentsButton from "./UploadDocumentsButton";
import { downloadAuthed, printElement } from "./printExport";
import SystemChips from "./SystemChips";
import ZoneTip from "./ZoneTip";
import ImportChecklist from "./ImportChecklist";
import RemoteShopDrawingsButton from "components/Integrations/RemoteShopDrawingsButton";
import LinkReview, { MismatchAlerts, ProgressControls, StatusLegend } from "./ProgressPanel";
import {
  CATEGORY_LABEL, drawnSystems, isCovered, noteGroups, overviewHash, parseOverviewHash, roomLabel, sortedLevels, statusByKey, stepZone, zonePageUrl, openNewView, ftInText } from "./glanceModel";
import "./glance.css";

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem(tokenRef)}` });

// The drafting face, loaded once. A CSS @import is dropped when CRA
// concatenates it into the middle of the main stylesheet.
const FONT_HREF = "https://fonts.googleapis.com/css2?family=Barlow+Semi+Condensed:wght@400;500;600&display=swap";
function useGlanceFont() {
  useEffect(() => {
    if (document.querySelector(`link[href="${FONT_HREF}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT_HREF;
    document.head.appendChild(link);
  }, []);
}

// A room's ceiling: read off the ceiling plan, or assumed and said so. Kept
// out of the component - each branch inside it counts against the hooks
// lint's path limit.
const ceilingOf = (room) => {
  if (room.ceilingFt != null) return `, ceiling ${ftInText(room.ceilingFt)}${room.ceilingSource ? ` (${room.ceilingSource})` : ""}`;
  if (room.ceilingAssumedFt != null) return `, ceiling about ${ftInText(room.ceilingAssumedFt)} (assumed from ${room.ceilingAssumedFrom}; no tag on the ceiling plan)`;
  return "";
};

export default function GlanceTab({ projectId, onOpenDashboard }) {
  const history = useHistory();
  useGlanceFont();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [time, setTime] = useState({ dayType: "weekday", hour: 10 });
  const [systemId, setSystemId] = useState(null);
  const [levelId, setLevelId] = useState(null);
  const [roomId, setRoomId] = useState(null);
  // Bumped after an upload, so the import checklist reads what was just filed.
  const [inputsKey, setInputsKey] = useState(0);
  // All systems, zones and progress (guide sections 9 and 13).
  const [mode, setMode] = useState("systems");
  const [activeIds, setActiveIds] = useState(null);
  const [progress, setProgress] = useState(null);
  const [progressDate, setProgressDate] = useState(null);
  const [tip, setTip] = useState(null);
  const [modelLayer, setModelLayer] = useState(null);   // the model's tracked elements drawn on the plates (ModelLayer)
  const lastTap = useRef(null);
  const rootRef = useRef(null);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    fetch(`${url}/projects/${projectId}/glance`, { headers: auth() })
      .then(async (r) => { const body = await r.json().catch(() => ({})); if (!r.ok) throw new Error(body.err || `HTTP ${r.status}`); return body; })
      .then((body) => { setData(body); setError(""); })
      .catch((e) => setError(e.message || "Could not load the overview"))
      .finally(() => setLoading(false));
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const manifest = data?.manifest;
  const drawn = useMemo(() => drawnSystems(manifest), [manifest]);
  const hasZones = drawn.length > 0 && (manifest?.zones || []).length > 0;
  const active = useMemo(() => activeIds || new Set(drawn.map((s) => s.id)), [activeIds, drawn]);

  // The URL hash carries mode, systems, hour, day, date and level: read once
  // when the data arrives (anything it names that does not exist is ignored),
  // then kept up to date without adding history entries.
  const hashRead = useRef(false);
  useEffect(() => {
    if (!manifest || hashRead.current) return;
    hashRead.current = true;
    const h = parseOverviewHash(typeof window !== "undefined" ? window.location.hash : "");
    if (h.mode === "day" && manifest.operations) setMode("day");
    else if (h.mode === "progress") setMode("progress");
    if (h.sys) { const ids = new Set(drawn.map((x) => x.id)); setActiveIds(new Set(h.sys.filter((x) => ids.has(x)))); }
    const days = manifest.operations?.dayTypes || ["weekday", "saturday", "sunday"];
    if (h.hour != null || (h.day && days.includes(h.day))) setTime((t) => ({ hour: h.hour != null ? h.hour : t.hour, dayType: h.day && days.includes(h.day) ? h.day : t.dayType }));
    if (h.date) setProgressDate(h.date);
    if (h.level && (manifest.levels || []).some((l) => l.id === h.level)) setLevelId(h.level);
    if (h.zone && (manifest.zones || []).some((z) => z.index === h.zone.zone)) {
      setTip({ level: h.zone.level, zone: h.zone.zone, x: null, y: null, href: zonePageUrl(projectId, zoneState(h.zone.level, h.zone.zone)) });
    }
  }, [manifest, drawn]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!manifest || !hashRead.current || typeof window === "undefined") return;
    const all = drawn.length && active.size === drawn.length;
    const pinned = tip && tip.x == null ? { level: tip.level, zone: tip.zone } : null;
    const next = overviewHash({ mode, sys: all ? null : [...active], hour: time.hour, day: time.dayType, date: progressDate, level: levelId, zone: pinned });
    if (next !== window.location.hash && !(next === "" && !window.location.hash)) {
      window.history.replaceState(window.history.state, "", `${window.location.pathname}${window.location.search}${next}`);
    }
  }, [manifest, drawn, active, mode, time, progressDate, levelId, tip]);

  // The documentation submenu: views generated from this overview.
  const [docViews, setDocViews] = useState([]);
  useEffect(() => {
    if (!projectId) return;
    fetch(`${url}/projects/${projectId}/glance/views`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : { views: [] })).then((b) => setDocViews(b.views || [])).catch(() => setDocViews([]));
  }, [projectId]);

  const loadProgress = useCallback(() => {
    if (!projectId) return;
    fetch(`${url}/projects/${projectId}/glance/progress`, { headers: auth() })
      .then((r) => (r.ok ? r.json() : null)).then((body) => setProgress(body)).catch(() => setProgress(null));
  }, [projectId]);
  useEffect(() => { if (hasZones) loadProgress(); }, [hasZones, loadProgress]);

  const date = progressDate || (progress && progress.today) || null;
  const statuses = useMemo(() => (mode === "progress" && progress && date ? statusByKey(progress, date) : null), [mode, progress, date]);
  const tally = useMemo(() => {
    const t = { todo: 0, prog: 0, done: 0, blocked: 0 };
    if (statuses && manifest) Object.keys(manifest.buckets || {}).forEach((k) => { if (statuses[k]) t[statuses[k].st] += 1; });
    return t;
  }, [statuses, manifest]);

  const levels = useMemo(() => sortedLevels(manifest), [manifest]);
  // Escape closes the zone card; while one is open, the arrow keys step to the
  // next zone (left/right) or level (up/down). Never while typing.
  const zoneStepRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setTip(null); return; }
      if (!/^Arrow(Left|Right|Up|Down)$/.test(e.key) || !zoneStepRef.current) return;
      const t = e.target; if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      if (zoneStepRef.current(e.key)) e.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const systems = manifest?.systems || [];
  // With drawn systems the chips choose what shows; the room highlight is
  // the system that has rooms (telecom on RT06).
  const roomSystems = hasZones ? systems.filter((s) => (s.rooms || []).length) : systems;
  const system = roomSystems.find((s) => s.id === systemId) || roomSystems[0] || null;
  const level = levels.find((l) => l.id === levelId) || levels[0] || null;
  const dayTypes = manifest?.operations?.dayTypes || ["weekday", "saturday", "sunday"];

  const roomsById = useMemo(() => {
    const map = {};
    levels.forEach((l) => l.rooms.forEach((r) => { map[r.id] = { ...r, levelName: l.name || l.id, levelId: l.id }; }));
    return map;
  }, [levels]);
  const room = roomId ? roomsById[roomId] : null;

  const selectRoom = (id, hoverOnly) => {
    setRoomId(id);
    if (!hoverOnly && roomsById[id]) setLevelId(roomsById[id].levelId);
  };

  const openFact = (link) => {
    if (link === "dashboard") return onOpenDashboard && onOpenDashboard();
    if (link === "issues") return history.push(`/issues?projectId=${projectId}`);
    if (link === "partners") return history.push(`/partners?projectId=${projectId}`);
    if (link === "areas") return history.push(`/areas/${projectId}`);
    if (link === "docs") return history.push("/document-control");
    return undefined;
  };

  const review = async (body) => {
    try {
      const r = await fetch(`${url}/projects/${projectId}/glance/review`, {
        method: "POST", headers: { "Content-Type": "application/json", ...auth() }, body: JSON.stringify(body),
      });
      const out = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(out.err || `HTTP ${r.status}`);
      message.success(body.decision === "accepted" ? "Accepted" : "Removed from the overview");
      load();
    } catch (e) { message.error(e.message); }
  };

  const zoneState = (level, zone) => ({ level, zone, sys: [...active] });
  zoneStepRef.current = tip ? (key) => {
    const next = stepZone(tip, key, levels.map((l) => l.id), (manifest?.zones || []).map((z) => z.index));
    if (!next) return false;
    setTip({ ...next, x: null, y: null, href: zonePageUrl(projectId, zoneState(next.level, next.zone)) });
    return true;
  } : null;
  const onZoneEnter = (level, zone, e, moving) => {
    if (typeof window !== "undefined" && window.innerWidth <= 700) return; // phones: tap instead
    if (moving && tip && tip.level === level && tip.zone === zone) return;
    setTip({ level, zone, x: e ? e.clientX : null, y: e ? e.clientY : null, href: zonePageUrl(projectId, zoneState(level, zone)) });
  };
  const onZoneLeave = () => { if (!(typeof window !== "undefined" && window.innerWidth <= 700)) setTip(null); };
  const onZoneOpen = (level, zone) => {
    const id = `${level}|${zone}`;
    const href = zonePageUrl(projectId, zoneState(level, zone));
    if (typeof window !== "undefined" && window.innerWidth <= 700 && lastTap.current !== id) {
      lastTap.current = id;
      setTip({ level, zone, x: null, y: null, href });
      return;
    }
    lastTap.current = null;
    window.open(href, "_blank", "noopener");
  };

  if (loading && !data) return <Skeleton active paragraph={{ rows: 10 }} />;
  if (error && !data) {
    return <div className="bl-glance"><div className="g-empty">{error} <Button size="small" onClick={load}>Retry</Button></div></div>;
  }
  if (!manifest) return null;

  const stored = data.stored;
  const hasBuilding = levels.length > 0;
  const status = manifest.operations ? statusSentence(manifest, time.dayType, time.hour) : "";
  const categories = [...new Set(levels.flatMap((l) => l.rooms.map((r) => r.category)).filter((c) => CATEGORY_LABEL[c]))];
  const stages = [...new Set((manifest.builtFrom || []).map((d) => d.stage).filter(Boolean))];
  const coverageLevels = [...levels.map((l) => l.id), ...(drawn.some((s) => s.id === "roof") ? ["roof"] : [])];
  const partial = drawn.filter((s) => s.coverage && coverageLevels.some((l) => (manifest.zones || []).some((z) => !isCovered(s, l, z.index))));
  const full = drawn.filter((s) => !partial.includes(s));
  const schematicBands = drawn.filter((s) => s.heightBasis === "schematic");

  // The construction lane: mapped activities as bars, and the slider's date.
  const timeline = (() => {
    const base = manifest.timeline;
    if (!base) return null;
    if (mode !== "progress" || !progress || !progress.span) return base;
    const acts = Object.values(progress.activities || {}).sort((a, b) => String(a.start).localeCompare(String(b.start))).slice(0, 8);
    const lane = acts.length ? {
      id: "progress:mapped", name: "Construction (mapped)",
      spans: acts.map((a) => ({ start: String(a.start).slice(0, 10), end: String(a.end).slice(0, 10), label: a.name, detail: `${a.progress || 0}% reported` })),
    } : null;
    const end = base.end && progress.span[1] > base.end ? progress.span[1] : base.end;
    const start = base.start && progress.span[0] < base.start ? progress.span[0] : base.start;
    return { ...base, start, end, lanes: lane ? [...base.lanes, lane] : base.lanes };
  })();

  return (
    <div className="bl-glance" ref={rootRef}>
      <h1 className="g-title">{manifest.title ? `${manifest.title} at a glance` : "At a glance"}</h1>
      {manifest.summary && (typeof manifest.summary === "string" || manifest.summary.text) && (
        <p className="g-sub g-summary">
          {typeof manifest.summary === "string" ? manifest.summary : manifest.summary.text}
          {manifest.summary.buildingType ? <span className="g-tsub"> · {manifest.summary.buildingType}</span> : null}
          {manifest.summary.review === "pending" && <span className="g-badge" title="Proposed automatically; confirm it in the Review Center">Pending</span>}
        </p>
      )}
      <div className="g-meta">
        <span>
          {stored
            ? `Building data: version ${stored.version}${stored.createdByName ? `, published by ${stored.createdByName}` : ""} on ${String(stored.updatedAt).slice(0, 10)}. Schedule, sheets and issues are live.`
            : "Schedule, sheets and issues are live. No building data has been published yet."}
        </span>
        <Button className="g-noprint" size="small" icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
        <Dropdown menu={{
          items: [
            ...docViews.map((v) => ({ key: v.viewId, label: `${v.title}${v.stale ? " (stale)" : ""}` })),
            ...(docViews.length ? [{ type: "divider" }] : []),
            { key: "views", label: docViews.length ? "All views" : "Technical documentation" },
            ...(data.canAdd ? [{ key: "views:new", label: "New view..." }] : []),
          ],
          onClick: ({ key }) => history.push(`/documentation/${projectId}?tab=${encodeURIComponent(key === "views:new" ? "views" : key)}${key === "views:new" ? "&new=1" : ""}`),
        }}>
          <Button size="small" icon={<FileTextOutlined />}>Technical documentation{docViews.length ? ` (${docViews.length})` : ""} <DownOutlined /></Button>
        </Dropdown>
        <span className="g-noprint g-actions">
          <Button size="small" icon={<PrinterOutlined />} onClick={() => printElement(rootRef.current, `${manifest.title || "Project"} at a glance`)}>Print</Button>
          <GlanceThemeToggle />
          <Tooltip title={stored ? "Summary, quantities of every system by level and zone, the item register and the rooms, as Excel" : "Nothing to export until building data is published"}>
            <Button size="small" icon={<FileExcelOutlined />} disabled={!stored}
                    onClick={() => downloadAuthed(`/projects/${projectId}/glance/export.xlsx`, `${String(manifest.title || "project").replace(/[^a-z0-9]+/gi, "-")}-at-a-glance.xlsx`).catch((e) => message.error(e.message))}>
              Export Excel
            </Button>
          </Tooltip>
        </span>
        {/* ONE branch for both buttons: this component sits at the hooks lint's code-path
            limit, and a second `data.canAdd &&` reads to it as a conditional hook. The second
            brings shop drawings in from the Procore / ACC shop drawings DOWNLOAD folders
            (owner 2026-10-25). */}
        {data.canAdd && (
          <>
            <UploadDocumentsButton projectId={projectId} projectTitle={manifest.title} onUploaded={() => setInputsKey((k) => k + 1)} />
            <RemoteShopDrawingsButton projectId={projectId} projectTitle={manifest.title} onFiled={() => setInputsKey((k) => k + 1)} />
          </>
        )}
      </div>

      <FactStrip facts={manifest.facts} onOpen={openFact} />

      {stages.length > 1 && (
        <p className="g-note">
          The documents are at different stages: {(manifest.builtFrom || []).filter((d) => d.stage).map((d) => `${d.label || d.docId} ${d.stage}`).join("; ")}.
        </p>
      )}

      {hasBuilding && hasZones ? (
        <section>
          <h2>Building systems, floor by floor</h2>
          <p className="g-note">
            Each floor is drawn from plan geometry with its systems layered on top. Hover a zone for a large exploded view of that part of
            the building, stacked by height. Click it to open the zone page in a new tab, with the same filters, a plan view, and every item the drawings list there.
          </p>
          <div className="g-seg" role="group" aria-label="View">
            <button type="button" aria-pressed={mode === "systems"} onClick={() => { setMode("systems"); setTip(null); }}>Systems</button>
            {manifest.operations && <button type="button" aria-pressed={mode === "day"} onClick={() => { setMode("day"); setTip(null); }}>A day in the building</button>}
            <button type="button" aria-pressed={mode === "progress"} onClick={() => { setMode("progress"); setTip(null); loadProgress(); }}>Construction progress</button>
          </div>
          <SystemChips manifest={manifest} active={active} onChange={setActiveIds} />
          {data.canEdit && active.size > 0 && active.size < drawn.length && (
            <div className="g-actions g-noprint">
              {/* Guide 20.3: a system chip is a short route to a view of that system, all levels, all zones. */}
              <button type="button" className="g-linkbtn" onClick={() => {
                const picked = drawn.filter((d) => active.has(d.id));
                openNewView(history, projectId, {
                  title: `${picked.map((d) => d.label).join(", ")}, whole building`,
                  request: { systems: picked.map((d) => d.id), levels: [], zones: [], context: ["pathways", "rooms"] },
                });
              }}>New view of {active.size === 1 ? drawn.find((d) => active.has(d.id)).label.toLowerCase() : `these ${active.size} systems`}</button>
            </div>
          )}
          {mode === "day" && manifest.operations && <DayControls dayTypes={dayTypes} dayType={time.dayType} hour={time.hour} onChange={setTime} status={status} />}
          {mode === "progress" && (
            <>
              {progress && progress.span && date && <ProgressControls progress={progress} date={date} onDate={setProgressDate} tally={tally} />}
              <MismatchAlerts progress={progress} />
              <LinkReview projectId={projectId} manifest={manifest} progress={progress} canEdit={data.canEdit} onChanged={loadProgress} />
            </>
          )}
          <div className="g-svgwrap">
            <ExplodedStack manifest={manifest} system={system} dayType={time.dayType} hour={time.hour} selectedRoomId={roomId} onSelectRoom={selectRoom}
                           active={active} mode={mode} statuses={statuses} activeZone={tip ? `${tip.level}|${tip.zone}` : null}
                           onZoneEnter={onZoneEnter} onZoneLeave={onZoneLeave} onZoneOpen={onZoneOpen} modelLayer={modelLayer} />
          </div>
          <ModelLayer projectId={projectId} onLayer={setModelLayer} />
          {mode === "progress" && <StatusLegend />}
          {mode === "day" && manifest.operations && (
            <>
              <div className="g-svgwrap"><DayProfile manifest={manifest} dayType={time.dayType} hour={time.hour} /></div>
              <RoofTable manifest={manifest} dayType={time.dayType} hour={time.hour} />
            </>
          )}
          <div className="g-legend">
            {categories.map((c) => <span key={c}><i style={{ background: `var(--${c})` }} />{CATEGORY_LABEL[c]}</span>)}
            {system && <span><i style={{ background: "var(--system)" }} />{system.label.replace(/ and pathways$/i, "")}</span>}
            {system && (system.stacks || []).length > 0 && <span><i className="g-key-riser" />Riser between stacked rooms</span>}
          </div>
          <ZoneTip manifest={manifest} tip={tip} active={active} statuses={statuses} mode={mode} progressDate={date} onClose={() => { setTip(null); lastTap.current = null; }} />
        </section>
      ) : hasBuilding ? (
        <section>
          <h2>{manifest.operations ? "The building through a day" : "The building"}</h2>
          <p className="g-note">
            {manifest.operations
              ? "Drag the clock to see how the building is used. Room shading follows the energy model's occupancy profile, rooftop units switch between occupied mode and setback on their schedule, and systems on a 24/7 schedule stay on."
              : "Floors stacked bottom to top. Select a room to see it on the floor plan below."}
            {system?.stacks?.length ? " The orange dashed lines are the riser stacks linking the highlighted rooms between floors." : ""}
          </p>
          {systems.length > 1 && (
            <div className="g-seg" role="group" aria-label="Highlighted system">
              {systems.map((s) => (
                <button key={s.id} type="button" aria-pressed={s.id === system?.id} onClick={() => setSystemId(s.id)}>{s.label}</button>
              ))}
            </div>
          )}
          {manifest.operations && <DayControls dayTypes={dayTypes} dayType={time.dayType} hour={time.hour} onChange={setTime} status={status} />}
          <div className="g-svgwrap">
            <ExplodedStack manifest={manifest} system={system} dayType={time.dayType} hour={time.hour} selectedRoomId={roomId} onSelectRoom={selectRoom} />
          </div>
          {manifest.operations && <div className="g-svgwrap"><DayProfile manifest={manifest} dayType={time.dayType} hour={time.hour} /></div>}
          <RoofTable manifest={manifest} dayType={time.dayType} hour={time.hour} />
          <div className="g-legend">
            {categories.map((c) => <span key={c}><i style={{ background: `var(--${c})` }} />{CATEGORY_LABEL[c]}</span>)}
            {system && <span><i style={{ background: "var(--system)" }} />{system.label}</span>}
          </div>
        </section>
      ) : (
        <section>
          <h2>The building</h2>
          <div className="g-empty">
            <b>No floor geometry for this project yet.</b> The stacked building, the operating clock and the floor plans appear
            once a manifest with levels and room outlines is published: from the Revit sync (room boundaries keyed by UniqueId) or
            from DWG floor plans. Nothing here is guessed, so until then this section stays empty.
            {data.canEdit ? " Upload the project's drawings, models and schedules above; the import checklist below shows what is still needed." : ""}
          </div>
        </section>
      )}

      {timeline && timeline.lanes.length > 0 && (
        <section>
          <h2>How the project got here</h2>
          <p className="g-note">
            Issue dates by discipline, schedule milestones, and the project stages as bars. Filled diamonds are done, hollow ones are still ahead.
            {" "}Select a schedule milestone to open it on the dashboard.
            {mode === "progress" && progress && progress.span ? " The construction lane shows the activities mapped to the building, and the dashed line follows the progress date above." : ""}
          </p>
          <div className="g-svgwrap">
            <TimelineLanes timeline={timeline} today={manifest.today}
                           marker={mode === "progress" && date ? { date, label: "Progress date" } : null}
                           onOpenActivity={(id) => onOpenDashboard && onOpenDashboard(id)} />
          </div>
        </section>
      )}

      {hasBuilding && level && (
        <section>
          <h2>Floor plans</h2>
          <p className="g-note">Hover or tap a room to see its name, number, and area. Plan north is up, matching the drawing sheets.{hasZones ? " The systems chosen above are drawn here too." : ""}</p>
          <div className="g-seg" role="group" aria-label="Choose level">
            {levels.map((l) => (
              <button key={l.id} type="button" aria-pressed={l.id === level.id} onClick={() => { setLevelId(l.id); setRoomId(null); }}>{l.name || l.id}</button>
            ))}
          </div>
          <div className="g-info" aria-live="polite">
            {room && room.levelId === level.id ? (
              <>
                <span>
                  <b>{roomLabel(room)}</b>, {room.levelName}{room.areaSf ? `, about ${Math.round(room.areaSf).toLocaleString()} sf` : ""}
                  {ceilingOf(room)}
                  {room.by === "claude" && room.review !== "accepted" ? <span className="g-badge" title="Room category proposed automatically; not yet reviewed">Pending</span> : null}
                </span>
                {(room.name || room.number) && (
                  // A new tab naming the room (searchLink.js); the search resolves it through GET /glance/locate.
                  <a href={roomSearchUrl(projectId, room)} target="_blank" rel="noopener noreferrer">
                    Find in model and sheets
                  </a>
                )}
              </>
            ) : (
              <span>
                {level.name || level.id}: {level.rooms.length} rooms
                {level.areaSf ? `, about ${Math.round(level.areaSf).toLocaleString()} sf` : ""}. Select a room.
              </span>
            )}
          </div>
          <div className="g-svgwrap">
            <PlanView manifest={manifest} level={level} system={system} selectedRoomId={roomId} onSelectRoom={selectRoom}
                      active={hasZones ? active : null} mode={mode} statuses={statuses} />
          </div>
        </section>
      )}

      {hasZones && (
        <section>
          <h2>What the drawings cover, and what they don't</h2>
          <div className="g-cols">
            {full.length > 0 && (
              <div><h3>Full building</h3><p>{full.map((s) => s.label).join(", ")} {full.length === 1 ? "is" : "are"} drawn for every level received.</p></div>
            )}
            {partial.map((s) => (
              <div key={s.id}>
                <h3>{s.coverage.note || "Part of the building"}</h3>
                <p>{s.label}: {s.source || "received drawings"}. Zones outside this say "Not in received drawings" until those sheets arrive.</p>
              </div>
            ))}
            {schematicBands.length > 0 && (
              <div>
                <h3>Heights</h3>
                <p>
                  {drawn.filter((s) => s.heightBasis === "documented").map((s) => `${s.label}: ${s.band}`).join("; ")}
                  {". "}{schematicBands.map((s) => s.label).join(", ")} {schematicBands.length === 1 ? "has" : "have"} no elevation in 2D drawings, so the zone view places {schematicBands.length === 1 ? "it" : "them"} schematically.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* The field's tracked categories (busway, tray, single line, branch,
          conduits, racks): shown only when the model has them. */}
      <TrackedCategories projectId={projectId} compact />

      {/* Notes with a group ("Where telecom lives") get their own heading; the
          rest are "What else the documents tell us". The notes keep their
          indexes, so a review still addresses the right one. */}
      {noteGroups(manifest.notes).map(({ group, notes }) => (
        <section key={group || "other"}>
          <h2>{group || "What else the documents tell us"}</h2>
          <SourceNotes notes={notes} canEdit={data.canEdit} onReview={review} />
        </section>
      ))}

      {data.canEdit && <ImportChecklist key={`${projectId}-${inputsKey}`} projectId={projectId} />}

    </div>
  );
}
